import { useState, useEffect, useCallback, useRef } from "react";
import NavigationDropdown from "../../components/NavigationDropdown";
import NavigationPanel from "../../components/NavigationPanel";
import PortalPopup from "../../components/PortalPopup";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import UploadDocument from "../../components/UploadDocument";
import UploadedDocument from "../../components/UploadedDocument";
import Breadcrumb from "../../components/Breadcrumb";
import Footer from "../../components/Footer";
import { getCurrentUser } from "../../utils/getCurrentUser";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { documentService } from "../../services/documentService";

const DocumentProcessing = () => {
  const [isNavigationDropdownOpen, setNavigationDropdownOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [lbaDetails, setLbaDetails] = useState([]);
  const [totalSubmitted, setTotalSubmitted] = useState(0);
  const [userId, setUserId] = useState(null);
  const [selectedDebtor, setSelectedDebtor] = useState(null);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [documents, setDocuments] = useState(null);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const user = getCurrentUser();
  const [isDebtorDropdownOpen, setDebtorDropdownOpen] = useState(false);
  const authToken = localStorage.getItem("authToken");

  const [documentText, setDocumentText] = useState("");
  const [detectedRisks, setDetectedRisks] = useState([]);
  const [debtorSearch, setDebtorSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const uploadedDocumentRef = useRef();

  useEffect(() => {
    if (uploadedDocumentRef.current) {
      uploadedDocumentRef.current.onDocumentsRefreshed = (data) => {
        const transformedDocuments = transformDocumentsData(data);
        setDocuments(transformedDocuments);
      };
    }
  }, [selectedDebtor]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const transformDocumentsData = (apiResponse) => {
    if (!apiResponse || !apiResponse.documents || apiResponse.documents.length === 0) {
      return { files: [], totalFiles: 0, createdAt: null };
    }
    const files = apiResponse.documents.map((doc) => ({
      fileName: doc.name,
      filePath: doc.fileUrl || `${API_URL}/api/official_documents/file/${doc.lbaId}/${doc.fileIndex}`,
      fileSize: doc.size,
      index: doc.fileIndex,
      mimeType: doc.mimeType,
      kind: doc.kind,
      icon: doc.icon,
    }));
    const latestCreatedAt = apiResponse.documents.reduce((latest, doc) => {
      if (!latest || new Date(doc.createdAt) > new Date(latest)) return doc.createdAt;
      return latest;
    }, null);
    return { files, totalFiles: files.length, createdAt: latestCreatedAt || new Date().toISOString() };
  };

  const handleDebtorSelect = (debtor) => {
    setDocuments(null);
    setIsLoadingDocuments(false);
    setSelectedDebtor(debtor);
    loadDocuments(debtor.id); // fixed: was debtor._id
  };

  const loadDocuments = useCallback(async (lbaId) => {
    if (!lbaId) { setDocuments(null); return; }
    setIsLoadingDocuments(true);
    setDocuments(null);
    try {
      const response = await documentService.getDocuments(lbaId);
      setDocuments(transformDocumentsData(response));
    } catch (error) {
      console.error("Error loading documents:", error);
      setDocuments({ files: [], totalFiles: 0, createdAt: null });
    } finally {
      setIsLoadingDocuments(false);
    }
  }, []);

  const closeNavigationDropdown = useCallback(() => setNavigationDropdownOpen(false), []);
  const closeLogoutConfirmation = useCallback(() => setLogoutConfirmationOpen(false), []);

  const handleUploadSuccess = useCallback(() => {
    if (selectedDebtor) loadDocuments(selectedDebtor.id); // fixed: was selectedDebtor._id
  }, [selectedDebtor, loadDocuments]);

  const handleDocumentRefresh = useCallback(() => {
    if (selectedDebtor) loadDocuments(selectedDebtor.id); // fixed: was selectedDebtor._id
  }, [selectedDebtor, loadDocuments]);

  const handleDownloadAll = useCallback(async () => {
    if (!selectedDebtor) { alert("Please select a debtor first"); return; }
    setIsDownloadingAll(true);
    try {
      if (!documents?.files?.length) { alert("No documents found to download"); return; }
      const zip = new JSZip();
      const debtorName = selectedDebtor.name.replace(/[^a-zA-Z0-9]/g, "_");
      const folder = zip.folder(`${debtorName}_Documents`);
      await Promise.all(documents.files.map(async (file) => {
        try {
          const fileResponse = await fetch(`${API_URL}/api/official_documents/file/${selectedDebtor.id}/${file.index}`); // fixed: was selectedDebtor._id
          if (!fileResponse.ok) throw new Error(`Failed to download ${file.fileName}`);
          folder.file(file.fileName, await fileResponse.blob());
        } catch (error) { console.error(`Error downloading ${file.fileName}:`, error); }
      }));
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `${debtorName}_documents_${new Date().toISOString().slice(0, 10)}.zip`);
    } catch (error) {
      alert("Failed to download documents. Please try again.");
    } finally {
      setIsDownloadingAll(false);
    }
  }, [selectedDebtor, documents, API_URL]);

  useEffect(() => {
    const storedUserId = user?.id;
    if (storedUserId) setUserId(storedUserId);
    const fetchTotalSubmitted = async () => {
      try {
        const response = await fetch(`${API_URL}/api/lba/data/${storedUserId}`, {
          method: "GET",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        });
        const data = await response.json();
        if (data.success) {
          setTotalSubmitted(data.count);
          setLbaDetails(data.details?.filter((item) => item.name && item.invoiceNumber) || []);
          setSelectedDebtor(null);
          setDocuments(null);
        }
      } catch (error) { console.error("Error fetching data:", error); }
    };
    fetchTotalSubmitted();
  }, [API_URL, user?.id, authToken]);

  useEffect(() => {
    if (!selectedDebtor) { setDocuments(null); setIsLoadingDocuments(false); }
  }, [selectedDebtor]);

  /* -----------------------------------------
     RENDER
  ----------------------------------------- */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=DM+Mono:wght@400;500&display=swap');

        html, body { background: rgb(241, 245, 249) !important; }

        .dp-root * { box-sizing: border-box; }
        .dp-root {
          font-family: 'DM Sans', sans-serif;
          background: rgb(241, 245, 249) !important;
          min-height: 100vh;
        }
        .dp-root.bg-white,
        .dp-root.bg-neutrals-neutral-50,
        .dp-root.bg-gray-50 { background: rgb(241, 245, 249) !important; }

        .dp-card {
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
          transition: box-shadow 0.2s ease; position: relative;
        }

        .dp-section-title { font-size: 16px; font-weight: 700; color: #0F172A; }
        .dp-section-sub   { font-size: 13px; color: #64748B; margin-top: 2px; }

        .dp-btn {
          display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #1E293B;
          cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          transition: border-color 0.15s, background 0.15s, color 0.15s;
        }
        .dp-btn:hover:not(:disabled) { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }
        .dp-btn:disabled { opacity: 0.45; cursor: not-allowed; }

        .dp-empty {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 56px 24px; text-align: center; gap: 8px;
        }
        .dp-empty-icon {
          width: 56px; height: 56px; border-radius: 16px; background: #EFF6FF;
          display: flex; align-items: center; justify-content: center; margin-bottom: 8px;
        }

        .dp-spinner {
          width: 20px; height: 20px;
          border: 2px solid #E2E8F0; border-top-color: #2563EB;
          border-radius: 50%; animation: dp-spin 0.7s linear infinite;
        }
        @keyframes dp-spin { to { transform: rotate(360deg); } }

        .dp-risk-table { width: 100%; border-collapse: collapse; margin-top: 8px; }
        .dp-risk-table th, .dp-risk-table td {
          padding: 10px 14px; text-align: left; border-bottom: 1px solid #F1F5F9;
          font-size: 13px;
        }
        .dp-risk-table th {
          font-size: 11px; font-weight: 700; color: #94A3B8;
          text-transform: uppercase; letter-spacing: 0.06em;
          background: #F8FAFC; border-bottom: 1px solid #E2E8F0;
        }

        .dp-count-pill {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 10px; background: #EFF6FF; border-radius: 20px;
          font-size: 12px; font-weight: 700; color: #2563EB;
        }

        .dp-breadcrumb { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #64748B; }
        .dp-breadcrumb a { color: #64748B; text-decoration: none; }
        .dp-breadcrumb a:hover { color: #2563EB; }
        .dp-breadcrumb .active { color: #0F172A; font-weight: 600; }
      `}</style>

      <div
        className="dp-root w-full overflow-visible"
        style={{ background: "rgb(241, 245, 249)", minHeight: "100vh" }}
      >
        <NavigationPanel />

        <div style={{ position: "absolute", top: 136, width: "100%", padding: "0 96px 64px", display: "flex", flexDirection: "column", gap: 28 }}>

          {/* Breadcrumb */}
          <div className="dp-breadcrumb">
            <a href="/">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M9.02 2.84L3.63 7.04C2.73 7.74 2 9.23 2 10.36v7.41C2 19.92 3.93 22 6.07 22h11.86C20.07 22 22 19.92 22 17.77V10.5c0-1.21-.81-2.76-1.8-3.45l-6.17-4.33c-1.4-.98-3.65-.93-5.01.12Z" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="m9 18 6-6-6-6" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span className="active">Document processing</span>
          </div>

          {/* Page header */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0F172A" }}>Document processing</h1>
              <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748B" }}>Upload, scan and manage documents for your debtors.</p>
            </div>
          </div>

          {/* Debtor selector card */}
          <div className="dp-card" style={{ padding: 24, overflow: 'visible', zIndex: 20, position: 'relative' }}>
            <div style={{ marginBottom: 16 }}>
              <div className="dp-section-title">Select debtor</div>
              <div className="dp-section-sub">Choose a debtor to view and upload their documents.</div>
            </div>

            <div ref={dropdownRef} style={{ position: "relative", width: 380 }}>
              {/* Trigger */}
              <button
                onClick={() => setDropdownOpen((o) => !o)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 14px", background: "#ffffff", border: "1px solid #E2E8F0",
                  borderRadius: 10, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14, color: selectedDebtor ? "#0F172A" : "#94A3B8", fontWeight: selectedDebtor ? 600 : 400,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  transition: "border-color 0.15s",
                  ...(dropdownOpen ? { borderColor: "#2563EB", boxShadow: "0 0 0 3px rgba(37,99,235,0.1)" } : {}),
                }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selectedDebtor ? `${selectedDebtor.name} — ${selectedDebtor.invoiceNumber}` : "Select a debtor…"}
                </span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                  style={{ flexShrink: 0, marginLeft: 8, transition: "transform 0.2s", transform: dropdownOpen ? "rotate(180deg)" : "none" }}>
                  <path d="m19 9-7 7-7-7" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {/* Dropdown panel */}
              {dropdownOpen && (
                <div style={{
                  position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
                  background: "#ffffff", border: "1px solid #E2E8F0", borderRadius: 12,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.12)", zIndex: 9999,
                  overflow: "hidden",
                }}>
                  {/* Search */}
                  <div style={{
                    padding: "10px 12px", borderBottom: "1px solid #F1F5F9",
                    display: "flex", alignItems: "center", gap: 8,
                    background: "#FAFBFD",
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M11.5 21a9.5 9.5 0 1 0 0-19 9.5 9.5 0 0 0 0 19ZM22 22l-2-2" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input
                      autoFocus
                      type="text"
                      placeholder="Type to search debtors…"
                      value={debtorSearch}
                      onChange={(e) => setDebtorSearch(e.target.value)}
                      style={{
                        border: "none", outline: "none", background: "transparent",
                        fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#0F172A", width: "100%",
                      }}
                    />
                    {debtorSearch && (
                      <button
                        onClick={() => setDebtorSearch("")}
                        style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#94A3B8", display: "flex", flexShrink: 0 }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Options list */}
                  <div style={{ maxHeight: 240, overflowY: "auto" }}>
                    {lbaDetails
                      .filter((d) => {
                        const q = debtorSearch.toLowerCase();
                        return (
                          d.name.toLowerCase().includes(q) ||
                          d.invoiceNumber.toLowerCase().includes(q)
                        );
                      })
                      .map((debtor) => {
                        const isSelected = selectedDebtor?.id === debtor.id; // fixed: was selectedDebtor?._id === debtor._id
                        return (
                          <div
                            key={debtor.id} // fixed: was debtor._id
                            onClick={() => { handleDebtorSelect(debtor); setDropdownOpen(false); setDebtorSearch(""); }}
                            style={{
                              padding: "11px 14px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
                              background: isSelected ? "#EFF6FF" : "transparent",
                              transition: "background 0.12s",
                            }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#F8FAFC"; }}
                            onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                          >
                            <div>
                              <div style={{ fontSize: 14, fontWeight: isSelected ? 700 : 600, color: isSelected ? "#2563EB" : "#0F172A" }}>
                                {debtor.name}
                              </div>
                              <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 1 }}>{debtor.invoiceNumber}</div>
                            </div>
                            {isSelected && (
                              <div style={{ width: 18, height: 18, background: "#2563EB", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                                  <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    {lbaDetails.filter((d) => {
                      const q = debtorSearch.toLowerCase();
                      return d.name.toLowerCase().includes(q) || d.invoiceNumber.toLowerCase().includes(q);
                    }).length === 0 && (
                      <div style={{ padding: "20px 14px", textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
                        No debtors match "{debtorSearch}"
                      </div>
                    )}
                  </div>

                  {/* Footer hint */}
                  {lbaDetails.length > 0 && (
                    <div style={{ padding: "8px 14px", borderTop: "1px solid #F1F5F9", background: "#FAFBFD", fontSize: 12, color: "#94A3B8" }}>
                      {lbaDetails.length} debtor{lbaDetails.length !== 1 ? "s" : ""} available
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Empty states */}
            {!selectedDebtor && lbaDetails.length === 0 && (
              <div className="dp-empty" style={{ paddingTop: 32 }}>
                <div className="dp-empty-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 22h6c5 0 7-2 7-7V9c0-5-2-7-7-7H9C4 2 2 4 2 9v6c0 5 2 7 7 7Z" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M15.75 9h-7.5M15.75 15h-7.5" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#0F172A" }}>No debtors found</div>
                <div style={{ fontSize: 13, color: "#64748B" }}>Please create a debtor first to upload documents.</div>
              </div>
            )}
            {!selectedDebtor && lbaDetails.length > 0 && (
              <div className="dp-empty" style={{ paddingTop: 32 }}>
                <div className="dp-empty-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9.5 14.5L11 16l3.5-4" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 2v3M16 2v3M3.5 9.09h17M21 8.5V17c0 3-1.5 5-5 5H8c-3.5 0-5-2-5-5V8.5c0-3 1.5-5 5-5h8c3.5 0 5 2 5 5Z" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#0F172A" }}>Select a debtor</div>
                <div style={{ fontSize: 13, color: "#64748B" }}>Use the dropdown above to pick a debtor and manage their documents.</div>
              </div>
            )}
          </div>

          {/* Upload card */}
          {selectedDebtor && (
            <div className="dp-card" style={{ overflow: "hidden", zIndex: 10 }}>
              <div style={{ padding: "18px 24px", borderBottom: "1px solid #E2E8F0" }}>
                <div className="dp-section-title">Upload documents</div>
                <div className="dp-section-sub">Attach invoices, contracts or legal documents for {selectedDebtor.name}.</div>
              </div>
              <div style={{ padding: 24 }}>
                <UploadDocument
                  lbaId={selectedDebtor.id}   // fixed: was selectedDebtor._id
                  name={selectedDebtor.name}
                  email={selectedDebtor.email}
                  onUploadSuccess={handleUploadSuccess}
                />
              </div>
            </div>
          )}

          {/* Scanned documents card */}
          {selectedDebtor && (
            <div className="dp-card" style={{ overflow: "hidden" }}>
              <div style={{ padding: "18px 24px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="dp-section-title">Scanned documents</span>
                  {!isLoadingDocuments && (
                    <span className="dp-count-pill">
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563EB", display: "inline-block" }}/>
                      {documents?.totalFiles || 0} {documents?.totalFiles === 1 ? "file" : "files"}
                    </span>
                  )}
                </div>
                <button
                  className="dp-btn"
                  onClick={handleDownloadAll}
                  disabled={isDownloadingAll || !documents?.files?.length}
                >
                  {isDownloadingAll ? (
                    <><div className="dp-spinner"/><span>Downloading…</span></>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M9 11v6l2-2M9 17l-2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Download all
                    </>
                  )}
                </button>
              </div>

              <div style={{ padding: 24 }}>
                {isLoadingDocuments ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "32px 0", color: "#64748B", fontSize: 14 }}>
                    <div className="dp-spinner"/>
                    Loading documents…
                  </div>
                ) : !documents?.files?.length ? (
                  <div className="dp-empty">
                    <div className="dp-empty-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "#0F172A" }}>No documents yet</div>
                    <div style={{ fontSize: 13, color: "#64748B" }}>Upload a document above to get started.</div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 18 }}>
                    <UploadedDocument
                      key={selectedDebtor.id}       // fixed: was selectedDebtor._id
                      ref={uploadedDocumentRef}
                      lbaId={selectedDebtor.id}     // fixed: was selectedDebtor._id
                      documents={documents}
                      onRefresh={handleDocumentRefresh}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Risk detection results */}
          {documentText && (
            <div className="dp-card" style={{ overflow: "hidden" }}>
              <div style={{ padding: "18px 24px", borderBottom: "1px solid #E2E8F0" }}>
                <div className="dp-section-title">Risk detection results</div>
                <div className="dp-section-sub">Highlighted clauses and phrases flagged by the AI scanner.</div>
              </div>
              <div style={{ padding: 24 }}>
                <div id="documentContent" style={{
                  background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10,
                  padding: 16, whiteSpace: "pre-wrap", fontSize: 14, color: "#1E293B",
                  minHeight: 200, lineHeight: 1.6, marginBottom: detectedRisks.length ? 24 : 0,
                }}>
                  {documentText}
                </div>

                {detectedRisks.length > 0 && (
                  <>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A", marginBottom: 12 }}>
                      Detected risks
                      <span className="dp-count-pill" style={{ marginLeft: 10 }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563EB", display: "inline-block" }}/>
                        {detectedRisks.length}
                      </span>
                    </div>
                    <table className="dp-risk-table">
                      <thead>
                        <tr><th>Phrase</th><th>Type</th><th>Score</th></tr>
                      </thead>
                      <tbody>
                        {detectedRisks.map((risk, i) => (
                          <tr key={i}>
                            <td style={{ fontWeight: 600, color: "#0F172A" }}>{risk.phrase}</td>
                            <td>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 6, background: "#FEF2F2", color: "#DC2626", fontSize: 12, fontWeight: 600 }}>
                                {risk.type}
                              </span>
                            </td>
                            <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{risk.score?.toFixed(2) ?? "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </div>
          )}

          <Footer />
        </div>
      </div>

      {isNavigationDropdownOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeNavigationDropdown}>
          <NavigationDropdown onClose={closeNavigationDropdown}/>
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeLogoutConfirmation}>
          <LogoutConfirmation onClose={closeLogoutConfirmation}/>
        </PortalPopup>
      )}
    </>
  );
};

export default DocumentProcessing;